// "+ Add surface" on the Condition tab (#468, #469). The surface joins the
// area's measured surfaces with nothing measured yet, so it waits on the
// Measure tab to be measured, and is rated on its line here. An "Other" is
// named first, in the same kind of name box as the Measure tab's, so each
// "Other" substrate has a rating of its own (condition-lines.ts). A line
// removed from the report is offered too, marked removed: picking it brings it
// back (#509).
import { useRef, useState } from 'react';
import { unitChoosable, type SurfaceType } from '@higgins/m2q-pricing';
import { addableTypes, type RatingLine } from '../lib/condition-lines';
import { addMeasuredSurface, renameMeasuredSurface } from '../lib/condition-records';
import { AddActionLabel } from './add-action';
import { SavedTextField, TEXT_MAX } from './saved-text-field';

/** `types`: the ones offered for the area's side; `lines`: the area's rating lines, removed ones included; `onRestore` brings a removed one back. */
export function AddConditionSurface({ areaId, types, lines, onRestore }: { areaId: string; types: readonly SurfaceType[]; lines: readonly RatingLine[]; onRestore: (line: RatingLine) => void }) {
  const [naming, setNaming] = useState<SurfaceType | null>(null);
  // The surface the name box made with its first save: later typing renames it, never adds another.
  const added = useRef<Promise<string> | null>(null);
  const addable = addableTypes(types, lines);
  const removed = lines.filter((l) => l.removed && l.observation);

  function choose(type: SurfaceType) {
    if (!unitChoosable(type)) return void addMeasuredSurface(areaId, type);
    added.current = null;
    setNaming(type);
  }

  async function saveName(type: SurfaceType, name: string) {
    if (added.current) return renameMeasuredSurface(areaId, await added.current, name);
    if (!name.trim()) return;
    added.current = addMeasuredSurface(areaId, type, name);
    await added.current;
  }

  if (naming) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Name the {naming.name.toLowerCase()} surface</span>
        <SavedTextField
          value=""
          autoFocus
          maxLength={TEXT_MAX}
          label={`Name for ${naming.name}`}
          placeholder="What is it? e.g. Bollards"
          className="min-w-48 flex-1 bg-white px-2 text-base md:text-base"
          onSave={(name) => saveName(naming, name)}
          onDone={() => setNaming(null)}
        />
      </div>
    );
  }
  return (
    <div className="mt-2">
      <label className="flex items-center gap-2 text-sm">
        <AddActionLabel>Add surface</AddActionLabel>
        <select
          value=""
          onChange={(e) => {
            const back = removed.find((l) => l.id === e.target.value);
            if (back) return onRestore(back);
            const type = addable.find((t) => t.id === e.target.value);
            if (type) choose(type);
          }}
          className="min-h-11 rounded-md border border-slate-300 bg-white px-2"
        >
          <option value="">Choose…</option>
          {addable.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
          {removed.length > 0 && (
            <optgroup label="Removed from the report">
              {removed.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} (removed)
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>
      <p className="mt-1 text-xs text-slate-500">A new surface is added to the Measure tab too, ready to measure.</p>
      {removed.length > 0 && <p className="text-xs text-slate-500">A removed line is brought back to the report as it was.</p>}
    </div>
  );
}
